import React, { useState } from "react";
import { Button, Tooltip } from "@heroui/react";
import { FileText } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/api/api.ts";

interface PdfDownloadButtonProps {
  animalId: string;
  gn: string;
  renderTrigger?: (props: {
    onPress: () => void;
    isLoading: boolean;
  }) => React.ReactNode;
}

const PdfDownloadButton: React.FC<PdfDownloadButtonProps> = ({ animalId, gn, renderTrigger }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleDownload = async () => {
    setIsLoading(true);
    const toastId = toast.loading("Generating Certificate...");

    try {
      const response = await apiClient.get(`/animals/generate-certificate/${animalId}`, {
        responseType: "blob", // Important for handling binary data
      });

      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      
      // Set filename
      const filename = `Sila_Certificate_${gn || animalId}.pdf`;
      link.setAttribute("download", filename);
      
      // Append to body, click, and cleanup
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Certificate downloaded successfully!", { id: toastId });
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Failed to generate certificate. Please try again.", { id: toastId });
    } finally {
      setIsLoading(false);
    }
  };

  if (renderTrigger) {
    return (
      <>
        {renderTrigger({ onPress: handleDownload, isLoading })}
      </>
    );
  }

  return (
    <Tooltip content="Download Certificate">
      <Button
        isIconOnly
        size="sm"
        variant="light"
        color="primary"
        aria-label="Download Certificate"
        onClick={handleDownload}
        isLoading={isLoading}
      >
        <FileText size={20} />
      </Button>
    </Tooltip>
  );
};

export default PdfDownloadButton;
